import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { RadioOrSelectOption } from '../../../../types/common.types';
import {
  HashTag,
  Member,
  MemberProfile,
  Post,
  PostWithHierarchy,
  SearchResult,
  TribeTemplate
} from '../../../../types/entity.types';

import { ARCHIVED, UPCOMING } from '../../../../utils/constants';
import { checkForTribeChanges } from '../../../../utils/diff.util';
import { noOp } from '../../../../utils/util';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';
import TribeContent from './tribe-content/TribeContent';

import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';

import './tribe.scss';

type Props = {
  status: string;
  tribeTemplate?: TribeTemplate;
  pendingApprovals?: number;
  posts?: Post[];
  memberPosts?: Post[];
  members?: Member[];
  hashtags?: HashTag[];
  profilePicture?: string;
  searchType?: RadioOrSelectOption;
  createHashtag: () => void;
  updateHashtag: (id: string | number, value: string) => void;
  deleteHashtag: (id: string | number) => void;
  createTribeTemplate: (template: TribeTemplate, url: string | undefined) => void;
  updateTribeTemplate: (template: TribeTemplate, url: string | undefined) => void;
  loadPosts: () => void;
  refreshPost: (postExternalReference: string) => void;
  clearPost: () => void;
  setSearchType: Dispatch<SetStateAction<RadioOrSelectOption>>;
  executeSearch: (searchValue: string) => void;
  postWithHierarchy?: PostWithHierarchy;
  searchResults?: SearchResult[];
  clearPostSearchResults: () => void;
  loadPostWithHierarchy?: (externalReference: string) => void;
  profile?: MemberProfile;
};

const Tribe = ({
  status,
  tribeTemplate,
  pendingApprovals = 0,
  posts = [],
  memberPosts = [],
  members = [],
  hashtags = [],
  profilePicture,
  searchType,
  createHashtag,
  updateHashtag,
  deleteHashtag,
  createTribeTemplate,
  updateTribeTemplate,
  loadPosts,
  refreshPost,
  clearPost,
  setSearchType,
  searchResults,
  clearPostSearchResults,
  executeSearch,
  loadPostWithHierarchy = noOp,
  postWithHierarchy,
  profile
}: Props) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [approvePosts, setApprovePosts] = useState(false);
  const [approvePhotos, setApprovePhotos] = useState(false);
  const [approveComments, setApproveComments] = useState(false);

  const navigate = useNavigate();

  const { memberExternalReference } = useParams();

  useEffect(() => {
    if (tribeTemplate) {
      setApprovePosts(tribeTemplate.approvePosts ?? false);
      setApprovePhotos(tribeTemplate.approvePhotos ?? false);
      setApproveComments(tribeTemplate.approveComments ?? false);
    }
  }, [tribeTemplate]);

  const checkForUnsavedChanges = (url: string) => {
    const tribeTemplateToSave: TribeTemplate = {
      ...tribeTemplate,
      approvePosts,
      approvePhotos,
      approveComments,
      hashTags: hashtags.map((hashtag) => ({ text: removeSpacesFromHashTag(hashtag.name ?? '') }))
    };

    if (checkForTribeChanges(tribeTemplate, tribeTemplateToSave)) {
      setRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const removeSpacesFromHashTag = (hashtag: string) => {
    return hashtag.replace(/\s/g, '');
  };

  const handleSaveClick = () => {
    const tribeTemplateToSave: TribeTemplate = {
      approvePosts,
      approvePhotos,
      approveComments,
      hashTags: hashtags.map((hashtag) => ({ text: removeSpacesFromHashTag(hashtag.name ?? '') }))
    };

    if (tribeTemplate?.externalReference) {
      updateTribeTemplate(
        {
          externalReference: tribeTemplate.externalReference,
          ...tribeTemplateToSave
        },
        redirectUrl
      );
    } else {
      createTribeTemplate(tribeTemplateToSave, redirectUrl);
    }
  };

  const handleDiscardClick = () => {
    setRedirectUrl(undefined);
    navigate(redirectUrl!);
  };

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="tribe" data-testid={`tribe-${status}`}>
        <div className="tribe__icons">
          {((settingsOpen && status !== ARCHIVED) || status === UPCOMING) && (
            <div className="tribe__icon-container" onClick={handleSaveClick}>
              <SaveIcon className="tribe__save-icon" data-testid="save-icon" />
            </div>
          )}
          {!settingsOpen && status !== UPCOMING && (
            <div className="tribe__icon-container" onClick={() => setSettingsOpen(true)}>
              <GearIcon className="tribe__gear-icon" data-testid="cog-icon" />
            </div>
          )}
          {settingsOpen && status !== UPCOMING && (
            <div className="tribe__icon-container" onClick={() => setSettingsOpen(false)}>
              <CloseIcon className="tribe__close-icon" data-testid="close-icon" />
            </div>
          )}
        </div>

        <TribeContent
          status={status}
          settingsOpen={settingsOpen}
          searchResults={searchResults}
          postWithHierarchy={postWithHierarchy}
          memberExternalReference={memberExternalReference}
          profilePicture={profilePicture}
          members={members}
          pendingApprovals={pendingApprovals}
          searchType={searchType}
          posts={posts}
          hashtags={hashtags}
          profile={profile}
          memberPosts={memberPosts}
          approvePosts={approvePosts}
          approvePhotos={approvePhotos}
          approveComments={approveComments}
          setSearchType={setSearchType}
          loadPostWithHierarchy={loadPostWithHierarchy}
          loadPosts={loadPosts}
          refreshPost={refreshPost}
          setApprovePosts={setApprovePosts}
          setApprovePhotos={setApprovePhotos}
          setApproveComments={setApproveComments}
          createHashtag={createHashtag}
          updateHashtag={updateHashtag}
          deleteHashtag={deleteHashtag}
          clearPost={clearPost}
          clearPostSearchResults={clearPostSearchResults}
          executeSearch={executeSearch}
        />
      </div>

      <SaveChangesModal
        show={!!redirectUrl}
        redirectUrl={redirectUrl}
        onSaveClick={handleSaveClick}
        onDiscardClick={handleDiscardClick}
      />
    </ProgrammeLayout>
  );
};

Tribe.defaultProps = {
  tribeTemplate: undefined,
  pendingApprovals: 0,
  posts: [],
  memberPosts: [],
  members: [],
  hashtags: [],
  profilePicture: undefined
};

export default Tribe;
